/**
 * Permission management utility for Vulture
 * 
 * This utility provides functions to check if a user has specific permissions
 * based on the response from broncoSDK.getUserPermissions()
 * 
 * Example API response:
 * {
 *   "role": "internal_admin",
 *   "permissions": [
 *     "tools_access",
 *     "user_management",
 *     "csv_upload",
 *     "settings_access",
 *     "programming",
 *     "property_access",
 *     "account_management",
 *     "pop_analytics"
 *   ],
 *   "access_limits": {
 *     "account_limited": false,
 *     "property_limited": false
 *   },
 *   "accessible_data": {
 *     "accounts": [
 *       "829a6946-828c-11eb-ae1a-0d442d14e026",
 *       "d8d22236-2426-46a4-a81d-6944886a54b8"
 *     ]
 *   },
 *   "component_access": [
 *     {
 *       "feature": "enable_brood_proof_of_play"
 *     }
 *   ]
 * }
 */

// Store the permissions data from the API
let permissionsData = null;

/**
 * Initialize the permissions with data from the API
 * @param {Object} data - Permissions data from broncoSDK.getUserPermissions()
 */
const initPermissions = (data) => {
  if (!data) {
    console.warn('Attempted to initialize permissions with empty data');
    return;
  }
  
  permissionsData = data;
};

/**
 * Check if the user has a specific permission
 * @param {string} permission - The permission key to check
 * @returns {boolean} - Whether the user has the specified permission
 */
const hasPermission = (permission) => {
  if (!permissionsData || !permissionsData.permissions) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return false;
  }

  return permissionsData.permissions.includes(permission);
};

/**
 * Check if the user has any of the specified permissions
 * @param {Array<string>} permissions - Array of permission keys to check
 * @returns {boolean} - Whether the user has any of the specified permissions
 */
const hasAnyPermission = (permissions) => {
  return permissions.some(permission => hasPermission(permission));
};

/**
 * Check if the user has all of the specified permissions
 * @param {Array<string>} permissions - Array of permission keys to check
 * @returns {boolean} - Whether the user has all of the specified permissions
 */
const hasAllPermissions = (permissions) => {
  return permissions.every(permission => hasPermission(permission));
};

/**
 * Check if the user has access to a specific account
 * @param {string} accountUUID - Account UUID to check
 * @returns {boolean} - Whether the user has access to the account
 */
const hasAccountAccess = (accountUUID) => {
  if (!permissionsData) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return false;
  }

  // If account access is not limited, user has access to all accounts
  if (!permissionsData.access_limits.account_limited) {
    return true;
  }

  // Otherwise, check if the account is in the accessible_data.accounts array
  return permissionsData.accessible_data && 
         permissionsData.accessible_data.accounts && 
         permissionsData.accessible_data.accounts.includes(accountUUID);
};

/**
 * Check if property access is limited for the user
 * @returns {boolean} - Whether property access is limited
 */
const isPropertyAccessLimited = () => {
  if (!permissionsData || !permissionsData.access_limits) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return true; // Default to limited access for safety
  }
  
  return permissionsData.access_limits.property_limited;
};

/**
 * Check if the user has a specific component/feature access
 * @param {string} featureName - The feature name to check
 * @returns {boolean} - Whether the user has access to the specified feature
 */
const hasFeatureAccess = (featureName) => {
  if (!permissionsData || !permissionsData.component_access) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return false;
  }

  return permissionsData.component_access.some(component => 
    component.feature === featureName
  );
};

/**
 * Get the user's role
 * @returns {string|null} - The user's role or null if not initialized
 */
const getUserRole = () => {
  if (!permissionsData) {
    console.warn('Permissions not initialized. Call initPermissions first.');
    return null;
  }
  
  return permissionsData.role;
};

/**
 * Get all accessible accounts for the user
 * @returns {Array<string>} - Array of accessible account UUIDs
 */
const getAccessibleAccounts = () => {
  if (!permissionsData || !permissionsData.accessible_data || !permissionsData.accessible_data.accounts) {
    return [];
  }
  
  return [...permissionsData.accessible_data.accounts];
};

/**
 * Check if permissions have been initialized
 * @returns {boolean} - Whether permissions have been initialized
 */
const isInitialized = () => {
  return permissionsData !== null;
};

/**
 * Get the raw permissions data
 * @returns {Object|null} - Raw permissions data or null if not initialized
 */
const getRawPermissions = () => {
  return permissionsData;
};

export {
  initPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasAccountAccess,
  isPropertyAccessLimited,
  hasFeatureAccess,
  getUserRole,
  getAccessibleAccounts,
  isInitialized,
  getRawPermissions,
};
